import { FileData, ICost, ICostingList, spLogTags } from "@eventr/common";
import { BoundStore, bs } from "./bound.store";
import { StateCreator } from "zustand";
import { DataAPI } from "../data/Data";
import { expenseLocalStore } from "feat/editItinerary/expenses/local-expense-store/local-expenses";
import { getModifiedTime } from "feat/editItinerary/expenses/expense-utils";
import type { DistanceExpenseFormValues } from "feat/editItinerary/expenses/forms/expense-form-utils";
import axios from "axios";
import { comboInterceptorReq } from "data/interseptors/comboInterceptorReq";
import { processFileForUpload } from "utils/file-utils";

// Draft key for the in-progress new distance expense. Existing expenses key their draft
// on the cost id.
export const NEW_DISTANCE_EXPENSE_DRAFT_KEY = "new";

// Stable empty constants so getSnapshot (useSyncExternalStore) never sees a new reference
// on the miss path. zustand 5 dropped useSyncExternalStoreWithSelector in favour of calling
// React's useSyncExternalStore directly, which means the selector now runs on every getSnapshot
// with no memoisation in front of it. A literal `?? []` / `?? {}` therefore hands React a new
// identity every call and triggers "Maximum update depth exceeded".
const NO_EXPENSES: Expense[] = [];
const EMPTY_EXPENSE_RECORD: Record<string, Expense> = Object.freeze({});

// fetchUserExpenses replaces the itinerary's whole record, from a GET that can take seconds.
// An add that lands in that window has already removed its local copy, and the GET left
// before the server had it, so the expense is in neither source and would be wiped out.
// Every upsert takes a sequence number; a fetch keeps whatever was upserted after it began.
let upsertSeq = 0;
const lastUpsertSeq = new Map<string, number>();

export type Expense = {
  cost: ICost;
  unopenedAiResponse?: boolean;
};

interface ExpensesState {
  expenses: Record<number, Record<string, Expense>>;
  expenseFormDrafts: Record<number, Record<string, DistanceExpenseFormValues>>;
}

interface ExpensesActions {
  fetchUserExpenses: () => Promise<void>;
  upsertExpense: (expense: Expense, itineraryId?: number) => void;
  getExpenses: () => Expense[];
  setExpenseFormDraft: (key: string, values: DistanceExpenseFormValues) => void;
  removeExpenseFormDraft: (key: string) => void;
}

export interface IExpensesSlice extends ExpensesState, ExpensesActions {}

export const createExpensesSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], IExpensesSlice> = (
  set,
  get,
) => ({
  expenses: {},
  expenseFormDrafts: {},

  fetchUserExpenses: async () => {
    const itinId = bs().selectedItinID;
    if (!itinId) return;
    const fetchStartSeq = upsertSeq;
    let remoteFetchSucceeded = false;
    //fetch expenses from database (remote)
    const fetchedRemoteExpenses: Record<string, Expense> = {};

    try {
      spLogTags(`[slice] Fetching expenses from database`, ["expenses"]);
      const resp = await DataAPI.expenses.getExpenses(itinId);

      if (resp?.status === "success" && resp.data) {
        remoteFetchSucceeded = true;
        const normalized = normalizeExpenses(resp.data);

        for (const costing of Object.values(normalized)) {
          for (const expense of costing.costs) {
            const existingExpense = get().expenses[itinId]?.[expense._id];
            //if expense has a legacy receipt_url we must convert it here and update it to the new standard
            //this way all expenses with receipt_url are converted on entry to the app.
            if (expense.receipt_url && !existingExpense?.cost.receipt_file) {
              spLogTags(`[slice] No receipt_file converting receipt_url to receipt_file`, ["expenses"]);
              const receiptFile = await convertReceiptUrlToReceiptFile(expense.receipt_url);
              expense.receipt_file = receiptFile;
            } else if (
              existingExpense?.cost.receipt_file &&
              !expense.receipt_file &&
              existingExpense.cost.receipt_url === expense.receipt_url
            ) {
              expense.receipt_file = existingExpense.cost.receipt_file;
            }
            const existingExpenseAiResponseTimestamp = existingExpense?.cost?.receipt_ai_data?.timestamp;
            const newExpenseAiResponseTimestamp = expense.receipt_ai_data?.timestamp;
            const existingUnopenedAiResponse = existingExpense?.unopenedAiResponse;
            const firstTimeSeeingExpense = !existingExpense;

            const unopenedAiResponse = firstTimeSeeingExpense
              ? false
              : existingExpenseAiResponseTimestamp !== newExpenseAiResponseTimestamp || existingUnopenedAiResponse;

            fetchedRemoteExpenses[expense._id] = {
              cost: expense,
              unopenedAiResponse,
            };
          }
        }
      }
    } catch {
      spLogTags(`[expenses] error fetching online expenses`, ["expenses"]);
    }

    //get local expenses from store
    spLogTags(`[slice] Getting local expenses from store`, ["expenses"]);
    const localExpensesFromStore = await expenseLocalStore.getByItineraryId(itinId);
    const fetchedLocalExpenses: Record<string, Expense> = {};

    for (const expenseFromStore of localExpensesFromStore) {
      if (expenseFromStore.data.itineraryId !== itinId) continue;
      const costId = expenseFromStore.data.cost._id;
      fetchedLocalExpenses[costId] = { cost: expenseFromStore.data.cost };
    }

    spLogTags(`[slice] Setting expenses`, ["expenses"]);

    const mergedExpenses = remoteFetchSucceeded
      ? mergeExpenseRecords(fetchedRemoteExpenses, fetchedLocalExpenses)
      : mergeExpenseRecords(get().expenses[itinId] ?? EMPTY_EXPENSE_RECORD, fetchedLocalExpenses);

    set((state) => ({
      expenses: {
        ...state.expenses,
        [itinId]: keepUpsertsSince(fetchStartSeq, state.expenses[itinId], mergedExpenses),
      },
    }));
  },

  upsertExpense: (expense, itineraryId) => {
    lastUpsertSeq.set(expense.cost._id, ++upsertSeq);
    set((state) => {
      const itinId = itineraryId ?? bs().selectedItinID;
      const itinExpenses = state.expenses[itinId] ?? {};
      const existingExpense = itinExpenses[expense.cost._id];
      return {
        expenses: {
          ...state.expenses,
          [itinId]: {
            ...itinExpenses,
            // Most callers pass a cost alone (a save response, a cached offline write) and
            // would otherwise drop a bobbin the list has not shown yet. The flag is only ever
            // derived in fetchUserExpenses or cleared on purpose when the AI dialog opens, so
            // it survives unless the caller states a value of its own.
            [expense.cost._id]: { unopenedAiResponse: existingExpense?.unopenedAiResponse, ...expense },
          },
        },
      };
    });
  },

  getExpenses: () => {
    const itinId = bs().selectedItinID;
    if (!itinId) return NO_EXPENSES;
    const expenses = get().expenses[itinId] ?? EMPTY_EXPENSE_RECORD;

    const filteredExpenses = Object.values(expenses).filter((e) => !e?.cost?.deleted);
    return filteredExpenses.sort((a, b) => getModifiedTime(b.cost) - getModifiedTime(a.cost));
  },

  setExpenseFormDraft: (key, values) => {
    const itinId = bs().selectedItinID;
    if (!itinId) return;
    set(
      (state) => ({
        expenseFormDrafts: {
          ...state.expenseFormDrafts,
          [itinId]: { ...(state.expenseFormDrafts[itinId] ?? {}), [key]: values },
        },
      }),
      false,
      "setExpenseFormDraft",
    );
  },

  removeExpenseFormDraft: (key) => {
    const itinId = bs().selectedItinID;
    if (!itinId) return;
    set(
      (state) => {
        const drafts = state.expenseFormDrafts[itinId];
        if (!drafts || !(key in drafts)) return {};
        const remaining = { ...drafts };
        delete remaining[key];
        return {
          expenseFormDrafts: {
            ...state.expenseFormDrafts,
            [itinId]: remaining,
          },
        };
      },
      false,
      "removeExpenseFormDraft",
    );
  },
});

// Export stable empty constant for use in selectors
export { EMPTY_EXPENSE_RECORD };

const normalizeExpenses = (expenses: unknown): ICostingList => {
  if (!expenses || Array.isArray(expenses)) return {};
  return expenses as ICostingList;
};

export type ExpenseFile = {
  base64: string;
  filename: string;
  mimeType: string;
};

const mergeExpenseRecords = (
  remoteExpenses: Record<string, Expense>,
  localExpenses: Record<string, Expense>,
): Record<string, Expense> => {
  const mergedExpenses: Record<string, Expense> = {};

  const allIds = new Set([...Object.keys(remoteExpenses), ...Object.keys(localExpenses)]);

  for (const id of allIds) {
    const remote = remoteExpenses[id];
    const local = localExpenses[id];

    if (remote && local) {
      //pick the one with the newest modified time
      mergedExpenses[id] = getModifiedTime(local.cost) > getModifiedTime(remote.cost) ? local : remote; // latest modified wins
    } else if (remote) {
      mergedExpenses[id] = remote;
    } else if (local) {
      mergedExpenses[id] = local;
    }
  }

  return mergedExpenses;
};

/** Carries over what was upserted after the fetch began, unless the fetch has a newer copy. */
const keepUpsertsSince = (
  fetchStartSeq: number,
  current: Record<string, Expense> | undefined,
  fetched: Record<string, Expense>,
): Record<string, Expense> => {
  if (!current) return fetched;
  let result = fetched;
  for (const [id, expense] of Object.entries(current)) {
    if ((lastUpsertSeq.get(id) ?? 0) <= fetchStartSeq) continue;
    const fetchedExpense = fetched[id];
    if (fetchedExpense && getModifiedTime(fetchedExpense.cost) > getModifiedTime(expense.cost)) continue;
    if (result === fetched) result = { ...fetched };
    result[id] = expense;
  }
  return result;
};

const convertReceiptUrlToReceiptFile = async (receiptUrl: string): Promise<FileData | undefined> => {
  try {
    const axiosInstance = axios.create({
      responseType: "blob",
      timeout: 15000,
    });
    axiosInstance.interceptors.request.use(comboInterceptorReq);

    const response = await axiosInstance.get(receiptUrl);
    if (!response?.data) return undefined;

    return await processFileForUpload(response.data);
  } catch {
    return undefined;
  }
};
